import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useNavigate, useSearchParams } from '../../lib/routing';
import { onboardingDoneTarget } from '../../lib/onboarding-return';
import { api } from '../../lib/api';

type DigitalComfort = 'debutant' | 'a_laise' | 'expert';
type TeachingStyle = 'magistral' | 'actif' | 'mixte';
type AiFrequency = 'quotidien' | 'hebdo' | 'ponctuel';
type Seniority = 'debutant' | 'experimente' | 'veteran';

type ChipGroupProps<T extends string> = {
  title: string;
  options: { value: T; label: string }[];
  selected: T | null;
  onSelect: (v: T) => void;
  disabled?: boolean;
};

function ChipGroup<T extends string>({ title, options, selected, onSelect, disabled }: ChipGroupProps<T>) {
  return (
    <div className="flex flex-col gap-[10px]">
      <div className="font-extrabold text-[13px] text-foreground tracking-[0.05em] uppercase">{title}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelect(opt.value)}
              disabled={disabled}
              aria-pressed={active}
              className={cn(
                "px-4 py-[10px] min-h-[44px] rounded-full border-[1.5px] border-naia-line-btn bg-card cursor-pointer font-bold text-sm text-foreground inline-flex items-center justify-center transition-colors disabled:opacity-[0.65] disabled:cursor-not-allowed",
                active && "bg-naia-navy border-[var(--naia-navy)] text-card"
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const SENIORITY_OPTIONS: { value: Seniority; label: string }[] = [
  { value: 'debutant',    label: 'Débutant (<3 ans)' },
  { value: 'experimente', label: 'Expérimenté' },
  { value: 'veteran',     label: 'Vétéran (>15 ans)' },
];

const STYLE_OPTIONS: { value: TeachingStyle; label: string }[] = [
  { value: 'magistral', label: 'Magistral' },
  { value: 'actif',     label: 'Actif' },
  { value: 'mixte',     label: 'Mixte' },
];

const COMFORT_OPTIONS: { value: DigitalComfort; label: string }[] = [
  { value: 'debutant', label: 'Débutant' },
  { value: 'a_laise',  label: "À l'aise" },
  { value: 'expert',   label: 'Expert' },
];

const FREQ_OPTIONS: { value: AiFrequency; label: string }[] = [
  { value: 'quotidien', label: 'Quotidien' },
  { value: 'hebdo',     label: 'Hebdomadaire' },
  { value: 'ponctuel',  label: 'Ponctuel' },
];

export default function TeacherOnboardingPreferences() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [seniority,      setSeniority]      = useState<Seniority | null>(null);
  const [teachingStyle,  setTeachingStyle]  = useState<TeachingStyle | null>(null);
  const [digitalComfort, setDigitalComfort] = useState<DigitalComfort | null>(null);
  const [aiFrequency,    setAiFrequency]    = useState<AiFrequency | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (withPrefs: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const prefs = withPrefs
        ? { seniority, teaching_style: teachingStyle, digital_comfort: digitalComfort, ai_frequency: aiFrequency }
        : null;
      await api.onboarding.complete.mutate({ preferences: prefs });
      navigate(onboardingDoneTarget(searchParams.get('next')), { replace: true });
    } catch {
      setError('Vérifiez votre connexion et réessayez.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[100dvh] pt-[max(0px,env(safe-area-inset-top))] pr-[max(20px,env(safe-area-inset-right))] pb-[max(24px,env(safe-area-inset-bottom))] pl-[max(20px,env(safe-area-inset-left))] bg-secondary">
      <div className="flex items-center justify-between py-3 shrink-0">
        <button type="button" onClick={() => navigate(-1)} className="w-11 h-11 flex items-center justify-center border-none bg-transparent cursor-pointer text-[22px] text-foreground font-bold rounded-sm" aria-label="Retour">
          ←
        </button>
        <h1 className="font-extrabold text-[18px] text-foreground m-0">Mieux vous connaître</h1>
        <div className="w-11" />
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col gap-6 pb-4">
        <p className="font-[500] text-sm text-muted-foreground leading-[1.6] m-0">
          Ces informations (facultatives) permettent à naia d'adapter ses suggestions à votre profil.
        </p>

        <ChipGroup title="Ancienneté" options={SENIORITY_OPTIONS} selected={seniority} onSelect={setSeniority} disabled={busy} />
        <ChipGroup title="Style pédagogique" options={STYLE_OPTIONS} selected={teachingStyle} onSelect={setTeachingStyle} disabled={busy} />
        <ChipGroup title="Confort numérique" options={COMFORT_OPTIONS} selected={digitalComfort} onSelect={setDigitalComfort} disabled={busy} />
        <ChipGroup title="Fréquence d'usage de l'IA" options={FREQ_OPTIONS} selected={aiFrequency} onSelect={setAiFrequency} disabled={busy} />

        {error && (
          <div className="bg-naia-error-bg rounded-[12px] px-[14px] py-[10px] border-[1.5px] border-naia-error">
            <p className="font-bold text-[13px] text-naia-error m-0">{error}</p>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 shrink-0 mt-2">
        <button
          type="button"
          onClick={() => submit(true)}
          disabled={busy}
          className="w-full px-5 py-4 bg-primary text-primary-foreground border-none rounded-full font-extrabold text-[16px] cursor-pointer min-h-[56px] tracking-[0.3px] transition-opacity disabled:opacity-[0.75] disabled:cursor-not-allowed"
        >
          {busy ? 'En cours…' : 'Terminer'}
        </button>
        <button
          type="button"
          onClick={() => submit(false)}
          disabled={busy}
          className="bg-transparent border-none font-bold text-sm text-muted-foreground cursor-pointer py-3 min-h-[44px] underline transition-opacity disabled:opacity-50"
        >
          Passer et accéder à naia
        </button>
      </div>
    </div>
  );
}
